<script setup>
import VDevider from "@/Shared/VDevider.vue";
import VButtonSubmit from "@/Shared/Buttons/VButtonSubmit";
import VInputWithBlockLabel from "../../Form/Survey/VInputWithBlockLabel.vue";

import { useForm } from "@inertiajs/vue3";
import VMultiText from "../../Form/Survey/VMultiText.vue";
import VMulti from "../../Form/Survey/VMulti.vue";
import VRadioTextWithBlockLabel from "../../Form/Survey/VRadioTextWithBlockLabel.vue";
import intus from "intus";
import { isRequired, isMin } from "intus/rules";
import isArrayNoEmptyValue from "../../../Plugins/rules/isArrayNoEmptyValue";

import { scrollOnError } from "@/Helpers/validation";

const props = defineProps({
    additional: Object,
    isShowSubmit: {
        Type: Boolean,
        default: true,
    },
});

const { surveyFields, answers } = props.additional;

const form = useForm({
    what_is_your_main_purpose_of_your_travel:
        answers?.what_is_your_main_purpose_of_your_travel ?? "",
    travel_companion: answers?.travel_companion ?? [],
    what_was_the_main_attraction_for_the_destinations_visited:
        answers?.what_was_the_main_attraction_for_the_destinations_visited ??
        [],
    where_did_you_stay_during_this_trip:
        answers?.where_did_you_stay_during_this_trip ?? [],
    how_many_nights_did_you_spend_at_the_destination:
        answers?.how_many_nights_did_you_spend_at_the_destination,
    where_did_you_travel_from: answers?.where_did_you_travel_from,
});

form.setError(props.additional.errors);

const emits = defineEmits("onNext");

const handleClickNext = () => {
    const validation = intus.validate(form.data(), {
        what_is_your_main_purpose_of_your_travel: [isRequired()],
        travel_companion: [isRequired()],
        what_was_the_main_attraction_for_the_destinations_visited: [
            isRequired(),
            isMin(1),
            isArrayNoEmptyValue(),
        ],
        where_did_you_stay_during_this_trip: [isRequired()],
        how_many_nights_did_you_spend_at_the_destination: [isRequired()],
        where_did_you_travel_from: [isRequired()],
    });

    form.clearErrors();
    if (validation.passes()) {
        emits("onNext", form.data());
    } else {
        form.setError(validation.errors());
        setTimeout(() => scrollOnError(), 300);
    }
};
</script>

<template>
    <div>
        <div class="row">
            <div class="col-12">
                <div class="mb-3">
                    <VRadioTextWithBlockLabel
                        elId="where_did_you_travel_from"
                        v-model:value="form.where_did_you_travel_from"
                        :label="surveyFields['where_did_you_travel_from']?.name"
                        :options="
                            surveyFields[
                                'where_did_you_travel_from'
                            ]?.options.map((value) => {
                                return { id: value, description: value };
                            })
                        "
                        :otherOptionLabel="
                            surveyFields['where_did_you_travel_from']?.config[
                                'other_label'
                            ]
                        "
                        :otherOptionPlaceholder="
                            surveyFields['where_did_you_travel_from']?.config[
                                'other_placehholder'
                            ]
                        "
                        :error="form.errors?.where_did_you_travel_from"
                    />
                </div>
                <div class="mb-3">
                    <VRadioTextWithBlockLabel
                        elId="what_is_your_main_purpose_of_your_travel"
                        v-model:value="
                            form.what_is_your_main_purpose_of_your_travel
                        "
                        :label="
                            surveyFields[
                                'what_is_your_main_purpose_of_your_travel'
                            ]?.name
                        "
                        :options="
                            surveyFields[
                                'what_is_your_main_purpose_of_your_travel'
                            ]?.options.map((value) => {
                                return { id: value, description: value };
                            })
                        "
                        :otherOptionLabel="
                            surveyFields[
                                'what_is_your_main_purpose_of_your_travel'
                            ]?.config?.other_label
                        "
                        :error="
                            form.errors
                                ?.what_is_your_main_purpose_of_your_travel
                        "
                    />
                </div>

                <div class="mb-3">
                    <VMulti
                        elId="travel_companion"
                        v-model:value="form.travel_companion"
                        :label="surveyFields['travel_companion']?.name"
                        :options="surveyFields['travel_companion']?.options"
                        :error="form.errors?.travel_companion"
                    />
                </div>

                <div class="mb-3">
                    <VMultiText
                        elId="what_was_the_main_attraction_for_the_destinations_visited"
                        v-model:value="
                            form.what_was_the_main_attraction_for_the_destinations_visited
                        "
                        :label="
                            surveyFields[
                                'what_was_the_main_attraction_for_the_destinations_visited'
                            ]?.name
                        "
                        :options="
                            surveyFields[
                                'what_was_the_main_attraction_for_the_destinations_visited'
                            ]?.options
                        "
                        :otherOptionLabel="
                            surveyFields[
                                'what_was_the_main_attraction_for_the_destinations_visited'
                            ]?.config?.other_label
                        "
                        :error="
                            form.errors
                                ?.what_was_the_main_attraction_for_the_destinations_visited
                        "
                    />
                </div>

                <div class="mb-3">
                    <VMulti
                        elId="where_did_you_stay_during_this_trip"
                        v-model:value="form.where_did_you_stay_during_this_trip"
                        :label="
                            surveyFields['where_did_you_stay_during_this_trip']
                                ?.name
                        "
                        :options="
                            surveyFields['where_did_you_stay_during_this_trip']
                                ?.options
                        "
                        :otherOptionLabel="
                            surveyFields['where_did_you_stay_during_this_trip']
                                ?.config?.other_label
                        "
                        :error="
                            form.errors?.where_did_you_stay_during_this_trip
                        "
                    />
                </div>
                <div class="mb-3">
                    <VInputWithBlockLabel
                        elId="how_many_nights_did_you_spend_at_the_destination"
                        v-model:value="
                            form.how_many_nights_did_you_spend_at_the_destination
                        "
                        :widthLabel="4"
                        :widthInput="8"
                        type="number"
                        :label="
                            surveyFields[
                                'how_many_nights_did_you_spend_at_the_destination'
                            ]?.name
                        "
                        :error="
                            form.errors
                                ?.how_many_nights_did_you_spend_at_the_destination
                        "
                    />
                </div>
            </div>
        </div>
        <VDevider />

        <div class="text-end" v-if="isShowSubmit">
            <VButtonSubmit type="button" @onCLickSubmit="handleClickNext">
                Next
            </VButtonSubmit>
        </div>
    </div>
</template>
